/**
 * Klickbare Telefonnummer. Ist Click-to-Dial eingerichtet, baut die
 * Telefonanlage den Anruf auf; sonst greift der tel:-Link des Geräts.
 */
import React from 'react';
import { Phone } from 'lucide-react';
import useClickToDial from '@/hooks/useClickToDial';

/** Abgeschnittene Klammern aus iMikel-Export entfernen (z. B. „…49 34 (“). */
export function cleanPhoneDisplay(value) {
  if (value == null || value === '') return '';
  let s = String(value).trim().replace(/\s*\([^)]*$/, '');
  return s.replace(/[(\s]+$/, '').trim();
}

/** Ziffern für tel:-URI; DE 0… → +49… */
export function toTelHref(phone) {
  if (!phone) return null;
  let d = cleanPhoneDisplay(phone);
  if (d.startsWith('+')) {
    d = d.replace(/^\+49/, '0').replace(/^\+/, '');
  }
  d = d.replace(/\D/g, '');
  if (!d) return null;
  if (d.startsWith('00')) return `tel:+${d.slice(2)}`;
  if (d.startsWith('0')) return `tel:+49${d.slice(1)}`;
  return `tel:+${d}`;
}

const HARD_SEPARATORS = /[,;|\r\n]+/;
/** Erst ab so vielen Ziffern gilt der bisherige Block als eigene Nummer (nicht nur Vorwahl). */
const MIN_DIGITS_BEFORE_SLASH_SPLIT = 8;

/**
 * Schrägstriche trennen in Deutschland oft Vorwahl und Rufnummer (01 59 / 01 64…),
 * deshalb reicht „nächster Teil beginnt mit 0“ nicht. Nur splitten, wenn der
 * bisherige Block schon eine vollständige Nummer ist — so bleibt
 * „01 59 / 01 64 97 18“ zusammen und
 * „0 59 22 / 77 93 88 / 01 52 / 24 86 50 14“ wird zu Festnetz + Mobil.
 */
function splitOnSlashes(part) {
  const segments = part.split('/');
  if (segments.length < 2) return [part.trim()];
  const numbers = [];
  let current = [];
  segments.forEach((segment, index) => {
    const soFar = current.join('/');
    const digitsSoFar = soFar.replace(/\D/g, '').length;
    if (
      index > 0
      && /^\s*[+0]/.test(segment)
      && current.length > 0
      && digitsSoFar >= MIN_DIGITS_BEFORE_SLASH_SPLIT
    ) {
      numbers.push(soFar.trim());
      current = [];
    }
    current.push(segment);
  });
  if (current.length > 0) numbers.push(current.join('/').trim());
  return numbers;
}

/** Einzelne Rufnummern aus einem Feld, das mehrere enthalten kann. */
export function splitPhoneNumbers(value) {
  if (!value) return [];
  return String(value)
    .split(HARD_SEPARATORS)
    .map((part) => cleanPhoneDisplay(part.trim()))
    .filter(Boolean)
    .flatMap(splitOnSlashes)
    .map((number) => cleanPhoneDisplay(number.trim()))
    .filter((number) => /\d/.test(number));
}

function SingleLink({ phone, className, showIcon, children, title, testId, available, dial }) {
  const href = toTelHref(phone);
  if (!href) {
    return <span className={className}>{children || phone}</span>;
  }

  const handleClick = (e) => {
    e.stopPropagation();
    if (!available) return;
    // Anlage übernimmt den Aufbau — der tel:-Link würde sonst zusätzlich starten.
    e.preventDefault();
    dial(phone, typeof children === 'string' ? children : undefined);
  };

  return (
    <a
      href={href}
      className={`inline-flex items-center gap-1 text-brand-red hover:underline ${className || ''}`}
      title={
        title || (available ? `Anrufen über die Telefonanlage: ${phone}` : `Anrufen: ${phone}`)
      }
      data-testid={testId}
      onClick={handleClick}
    >
      {showIcon && <Phone className="w-3.5 h-3.5 shrink-0" />}
      <span>{children || phone}</span>
    </a>
  );
}

export default function PhoneLink({
  phone,
  className = '',
  showIcon = false,
  children,
  title,
  'data-testid': testId,
}) {
  const { available, dial } = useClickToDial();
  if (!phone) return null;

  const displayPhone = cleanPhoneDisplay(phone);
  if (!displayPhone) return null;

  // Eigener Text steht für genau einen Anruf — dann nicht aufteilen.
  const numbers = children ? [] : splitPhoneNumbers(displayPhone);

  if (numbers.length > 1) {
    return (
      <span
        className={`inline-flex flex-col items-start gap-0.5 ${className}`}
        data-testid={testId || 'phone-links'}
      >
        {numbers.map((number, index) => (
          <SingleLink
            key={`${number}-${index}`}
            phone={number}
            showIcon={showIcon}
            title={title}
            testId={testId ? `${testId}-${index + 1}` : 'phone-link'}
            available={available}
            dial={dial}
          />
        ))}
      </span>
    );
  }

  return (
    <SingleLink
      phone={displayPhone}
      className={className}
      showIcon={showIcon}
      title={title}
      testId={testId || 'phone-link'}
      available={available}
      dial={dial}
    >
      {children}
    </SingleLink>
  );
}
